import { debounceTime, filter, map, Observable, switchMap, tap } from 'rxjs';
import { FeatureFlagService } from './../../../../core/services/feature-flag.service';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { OverlayRef } from '@angular/cdk/overlay';
import { MatLegacyMenu as MatMenu } from '@angular/material/legacy-menu';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Select, Store } from '@ngxs/store';

import { AccountService, AuthService } from 'app/core';
import { User, UserAssociation } from 'app/core/models';
import { UserService } from 'app/layout/common/user/user.service';
import { Account } from 'app/core/models/account.types';
import { Property } from 'app/modules/projects/projects.types';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { PropertyChangeTemplateComponent } from '../../modals/property-change-template/property-change-template.component';
import { FeatureFlag } from 'app/core/enums/feature-flag';
import { FileFormat, RelatedToType } from 'app/shared/enums';
import { generateDownloadableFile } from 'app/shared/functions';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { ObjectsManagerActions } from 'app/modules/settings/objects-manager/state/actions';
import { GenerateMergeDocumentComponent } from 'app/shared/modals/generate-merge-document/generate-merge-document.component';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { Module, NewPermission, Permission } from 'app/core/enums';
import { AccountState } from '@state/account/state';
import { UserState } from '@state/user/state';
import { hideCustomObjectTabBasedOnArchivedLinkedField } from '@shared/modules/custom-objects/custom-objects.functions';
import { TaskUtils } from '@tasks/tasks.utils';
import { TaskState } from '@tasks/state/state';

@Component({
  selector: 'property-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PropertiesHeaderComponent extends OnDestroyMixin implements OnInit, AfterViewInit {
  @ViewChild('matMenu', { static: true }) matMenu: MatMenu;

  @ViewChild('headerContainer') private headerContainer: ElementRef;
  @ViewChild('rightContainer') private rightContainer: ElementRef;
  @ViewChild('menuContainer') private menuContainer: ElementRef;
  @ViewChild('leftContainer') private leftContainer: ElementRef;

  public property: Property;
  public isCurrentUserAdmin: boolean = false;
  public currentUser: User;
  public propertiesRoute = '/properties';
  protected readonly Permission = Permission;
  protected readonly FeatureFlag = FeatureFlag;
  propertyList: number[];

  private _userAssociationsPanelOverlayRef: OverlayRef;

  data: any;
  account: Account;
  accountUserAssociations: UserAssociation[];
  userAssociations: UserAssociation[];
  projectUserIds: any[] = [];
  userRole: string;

  relatedToTypeProperty = RelatedToType.Property;
  customObjectsTabs: CustomObject[] = [];
  visibleCustomObjectsTabs: CustomObject[] = [];
  hiddenCustomObjectsTabs: CustomObject[] = [];
  selectedCustomObjectTab: CustomObject;
  hasDropDown = false;
  currentPropertyIndex: number;
  prev: number;
  next: number;
  NewPermission = NewPermission;
  isNewPermissionEnabled = false;
  tasks$ = this.store.select(TaskState.getTasks).pipe(
    map((t) => {
      const flat = TaskUtils.flattenTasks(t).filter((task) => task.task_type !== 'section');
      return flat;
    }),
  );

  get hasCustomObjectsEnabled$(): Observable<boolean> {
    return this._featureFlagService.hasFeatureFlag(FeatureFlag.CUSTOM_OBJECTS);
  }

  @Select(ObjectsManagerState.get) get$: Observable<CustomObject[]>;

  constructor(
    private _propertiesService: PropertiesService,
    private _userService: UserService,
    private _router: Router,
    private _matDialog: MatDialog,
    private _snackBar: MatSnackBar,
    private _changeDetectorRef: ChangeDetectorRef,
    private _activatedRoute: ActivatedRoute,
    private _accountService: AccountService,
    private _featureFlagService: FeatureFlagService,
    private confirmService: ConfirmService,
    private readonly store: Store,
    private readonly authService: AuthService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.isNewPermissionEnabled = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS));
    this._propertiesService.properties$.pipe(untilComponentDestroyed(this)).subscribe((properties: Property[]) => {
      if (properties) {
        this.propertyList = properties.map((i) => +i.id);
      }
    });

    this._propertiesService.property$.pipe(untilComponentDestroyed(this)).subscribe((property: Property) => {
      this.property = property;
      this.setPropertyRoute();

      if (this.propertyList) {
        this.currentPropertyIndex = this.propertyList.findIndex((i) => i === +property.id);
        this.prev = this.propertyList[this.currentPropertyIndex - 1];
        this.next = this.propertyList[this.currentPropertyIndex + 1];
      }

      this._changeDetectorRef.markForCheck();
    });

    this._activatedRoute.data.subscribe((data: Data) => {
      this.data = data;
    });

    this._accountService.currentAccount.subscribe((accountData) => {
      this.account = accountData;
    });

    this._accountService.userAssociations$
      .pipe(untilComponentDestroyed(this))
      .subscribe((userAssociations: UserAssociation[]) => {
        this.accountUserAssociations = userAssociations;
        this._checkIfAdmin();
      });

    this._userService.role$.pipe(untilComponentDestroyed(this)).subscribe((role: string) => {
      if (role) {
        this.userRole = role;
      }
      this._changeDetectorRef.markForCheck();
    });

    this._userService.user$.pipe(untilComponentDestroyed(this)).subscribe((user: User) => {
      if (user) {
        this.currentUser = user;
        this._checkIfAdmin();
      }
      this._changeDetectorRef.markForCheck();
    });

    this.onCreateCustomObjectsTabs();
  }

  ngAfterViewInit(): void {
    window.onresize = () => {
      this.handleCOLinkMenuResponsiveness();
    };
  }

  handleCOLinkMenuResponsiveness(): void {
    if (!this.headerContainer) return;

    const headerWidth = this.headerContainer.nativeElement.offsetWidth * 0.7;

    const rightWidth = [...this.rightContainer.nativeElement.children]?.reduce((acc, child) => {
      return acc + child.offsetWidth;
    }, 0);

    const menuWidth = [...this.menuContainer.nativeElement.children]?.reduce((acc, child) => {
      return acc + child.offsetWidth;
    }, 0);

    const leftWidth = [...this.leftContainer.nativeElement.children]?.reduce((acc, child) => {
      return acc + child.offsetWidth;
    }, 0);

    const availableWidth = headerWidth - rightWidth - menuWidth - leftWidth;

    const customTabsWidth = this.customObjectsTabs?.reduce((acc, tab) => {
      return acc + tab.name.length * 10;
    }, 0);

    if (customTabsWidth > availableWidth) {
      this.hasDropDown = true;
      let remainingWidth = availableWidth;
      this.visibleCustomObjectsTabs = this.customObjectsTabs.filter((tab) => {
        if (remainingWidth > tab.name.length * 10) {
          remainingWidth -= tab.name.length * 10;
          return true;
        }
        return false;
      });

      this.hiddenCustomObjectsTabs = this.customObjectsTabs.filter((tab) => {
        return !this.visibleCustomObjectsTabs.includes(tab);
      });
    } else {
      this.hasDropDown = false;
      this.visibleCustomObjectsTabs = this.customObjectsTabs;
    }

    if (this.hiddenCustomObjectsTabs?.length) {
      this.selectCustomObjectTab(this.hiddenCustomObjectsTabs[0]);
    }

    this._changeDetectorRef.markForCheck();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this._userAssociationsPanelOverlayRef) {
      this._userAssociationsPanelOverlayRef.dispose();
    }
  }

  setPropertyRoute(): void {
    this.propertiesRoute = '/properties/' + this.property.id;
  }

  goToPrevious(): void {
    this._router.navigate(['/properties/' + this.prev]);
  }

  goToNext(): void {
    this._router.navigate(['/properties/' + this.next]);
  }

  private _checkIfAdmin(): void {
    if (this.currentUser && this.accountUserAssociations) {
      const foundUserAssociations = this.accountUserAssociations.filter((ua) => {
        return ua.user.id == this.currentUser.id;
      });
      if (foundUserAssociations && foundUserAssociations.length > 0) {
        this.isCurrentUserAdmin = true;
      }
    }
  }

  public deleteProperty(): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(filter((res: boolean) => res))
      .subscribe(() => {
        const id = this.property.id;

        this._propertiesService.deleteProperty(id).subscribe(() => {
          const route = this._activatedRoute;

          //be sure that we update the recent projects when a project has been deleted
          this._userService.getRecentProperties(this.account.id).subscribe();

          if (this._router.url.endsWith('overview')) {
            this._router.navigate(['../../'], { relativeTo: route });
          } else {
            this._router.navigate(['../'], { relativeTo: route });
          }
        });

        this._changeDetectorRef.markForCheck();
      });
  }

  public changePropertyTemplate(): void {
    const dialogRef = this._matDialog.open(PropertyChangeTemplateComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { template_id: this.property?.template?.id, options: this.data?.options, related_to_type: 'Property' },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.template_id) {
        this.property.template_id = result.template_id;
        this._propertiesService.saveProperty(this.property).subscribe(() => {
          this._router.navigate([`./overview`], { relativeTo: this._activatedRoute });
        });
      }
    });
  }

  onCreateCustomObjectsTabs(): void {
    this.hasCustomObjectsEnabled$
      .pipe(
        untilComponentDestroyed(this),
        filter((hasCustomObjectsEnabled) => hasCustomObjectsEnabled),
        tap(() => this.store.dispatch(new ObjectsManagerActions.Get())),
        switchMap(() => this.get$.pipe(debounceTime(100))),
      )
      .subscribe((objects) => {
        if (!objects?.length) return;
        const parentObject = objects.find((object) => object.name === RelatedToType.Property);
        this.customObjectsTabs = this.store.selectSnapshot(ObjectsManagerState.getLinkedObjects(parentObject));
        this.handleCOLinkMenuResponsiveness();
      });
  }

  openGenerateModal(): void {
    const dialogRef = this._matDialog.open(GenerateMergeDocumentComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: {},
      width: '40vw',
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        if (result?.file_resource_id) {
          if (result.action && result.action === 'generate') {
            this.generatePropertyPdf(result.format, result.file_resource_id, result.file_name);
          } else if (result.action && result.action === 'docusign') {
            this.generateDocusign(result.format, result.file_resource_id);
          }
        }
      });
  }

  generatePropertyPdf(format: string = 'pdf', fileResourceId: number = null, fileName: string = null): void {
    this._snackBar.open('Download will begin shortly.', '', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
    this._propertiesService
      .generatePropertyDocument(this.property.id, format, fileResourceId)
      .pipe(untilComponentDestroyed(this))
      .subscribe((response) => {
        if (response.data) {
          if ([FileFormat.PDF, FileFormat.DOCX].includes(format as FileFormat)) {
            generateDownloadableFile(response.data, format as FileFormat, `${this.property.name} - ${fileName}`);
          }
        }
      });
  }

  generateDocusign(format: string = 'pdf', fileResourceId: number = null): void {
    this._snackBar.open('Docusign will open shortly.', '', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });

    this._propertiesService
      .generatePropertyDocument(this.property.id, format, fileResourceId, true)
      .pipe(untilComponentDestroyed(this))
      .subscribe((response) => {
        if (response.data) {
          if (response.url) {
            window.open(response.url, '_blank');
          } else {
            if ([FileFormat.PDF, FileFormat.DOCX].includes(format as FileFormat)) {
              generateDownloadableFile(response.data, format as FileFormat, `${this.property.name}`);
            }
          }
        }
      });
  }

  selectCustomObjectTab = (tab: CustomObject, clicked?: boolean): void => {
    this.selectedCustomObjectTab = tab;

    if (clicked) {
      this._router.navigate([this.propertiesRoute + '/custom_object', this.selectedCustomObjectTab.id], {
        relativeTo: this._activatedRoute,
        queryParams: { related_to_type: this.relatedToTypeProperty, related_to_id: this.property.id },
      });
    }
  };

  hasCustomObjectPermission(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return true;
    }

    return this.store.selectSnapshot(UserState.hasPermission(Permission.CUSTOM_OBJECTS_TAB));
  }

  hasPermission(permission: Permission): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return true;
    }

    return this.store.selectSnapshot(UserState.hasPermission(permission));
  }

  canShowThreeDotMenu(property: Property): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return (
        this.store.selectSnapshot(UserState.hasPermission(NewPermission.PROPERTIES_SHARING_RECORDS)) ||
        (this.store.selectSnapshot(UserState.hasPermission(NewPermission.PROPERTIES_DELETE)) &&
          property.permissions?.is_deletable)
      );
    }

    return true;
  }

  getDeletePropertyPermission(): [NewPermission, boolean] | Permission {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return [NewPermission.PROPERTIES_DELETE, this.property.permissions?.is_deletable];
    }

    return Permission.PROPERTY_DELETE;
  }

  canShowTaskTab(): boolean {
    if (this.isNewPermissionEnabled) {
      return true;
    }

    return this.authService.hasAnyPermission(Permission.PROPERTY_TASKS_TAB, Permission.PRIVATE_PROPERTY_TASKS);
  }

  canAccessNotes() {
    if (this.isNewPermissionEnabled) {
      return true;
    }

    return this.store.selectSnapshot(UserState.hasPermission(Permission.PROPERTY_NOTES_TAB));
  }

  canAccessLeases() {
    if (this.isNewPermissionEnabled) {
      return this.authService.hasAnyPermissionInModule(Module.LEASES);
    }
    const leass = this.store.selectSnapshot(UserState.hasPermission(Permission.PROPERTY_LEASES_TAB));
    return leass;
  }

  canAccessDeals() {
    if (this.isNewPermissionEnabled) {
      return this.authService.hasAnyPermissionInModule(Module.DEALS);
    }

    return this.store.selectSnapshot(UserState.hasPermission(Permission.PROPERTY_DEALS_TAB));
  }
}
